import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { TSupportedLanguages } from 'src/components/Header/LanguageSelect/LanguageSelect.types';
import {
  EActiveTabState,
  EResultsTab,
} from 'src/components/MapSidebar/components/MapSidebarSingleCommunity';
import { EModalAssetsManagerView } from 'src/components/ModalAssetsManager';
import { MOBILE_WIDTH } from 'src/constants/application';
import { DataAggregationResolution, Maybe } from 'src/graphql';
import { EDashboard, EFormVariant, EGridMarketTab } from 'src/typings/base-types';
import { EScreenMode, EScreenOrientation } from 'src/typings/configuration.types';
import { EAppMode, getAppMode } from 'src/utils/appMode';

export type TApplicationState = {
  /* Modals */
  modalAssetManagerActiveView: EModalAssetsManagerView;
  modalAssetManagerShowGuide: boolean;
  modalCommunitySummaryView: EModalAssetsManagerView;
  modalHouseSummaryView: EModalAssetsManagerView;
  modalHouseSummaryFormVariant: EFormVariant;
  /* Navigation Tabs */
  activeGridMarketTab: EGridMarketTab;
  activeSidebarMainTab: EActiveTabState;
  activeSidebarSettingsSubTab: EFormVariant;
  activeResultsTab: EResultsTab;
  /* Selected Inputs */
  selectedMemberUuid: string | null;
  selectedStartDatetime: string | null;
  selectedEndDatetime: string | null;
  selectedDataResolution: DataAggregationResolution;
  /* Others */
  isCustomHouseAddingContinuation: boolean;
  canaryWaitlistDismissed: boolean;
  modalMapAlertDismissed: boolean;
  sidebarExpanded: boolean;
  isEmbed: boolean;
  appMode: EAppMode;
  newCommunity: boolean;
  step: number;
  guideStep: number;
  tutorialStep: number;
  communityNotFound: boolean;
  userCreatedCommunityBefore: boolean;
  activeDashboard: EDashboard | undefined;
  activeGridMarketForm: EFormVariant;
  addNewLibrary: boolean;
  collaborationStep: number;
  isBottomRightCopyrightVisible: boolean;
  language: TSupportedLanguages;
  screenMode: EScreenMode;
  screenOrientation: Maybe<EScreenOrientation>;
};

export const initialApplicationState = {
  /* Modals */
  modalAssetManagerActiveView: EModalAssetsManagerView.AddCommunity as const,
  modalAssetManagerShowGuide: true,
  modalCommunitySummaryView: EModalAssetsManagerView.SaveOrSettings as const,
  modalHouseSummaryView: EModalAssetsManagerView.Summary as const,
  modalHouseSummaryFormVariant: EFormVariant.Express as const,
  /* Navigation Tabs */
  activeGridMarketTab: EGridMarketTab.Market,
  activeSidebarMainTab: EActiveTabState.Settings,
  activeSidebarSettingsSubTab: EFormVariant.Express,
  activeResultsTab: EResultsTab.Home,
  /* Selected Inputs */
  selectedMemberUuid: null,
  selectedStartDatetime: null,
  selectedEndDatetime: null,
  selectedDataResolution: DataAggregationResolution.Daily,
  /* Others */
  isCustomHouseAddingContinuation: false,
  canaryWaitlistDismissed: false,
  modalMapAlertDismissed: false,
  sidebarExpanded: false,
  isEmbed: false,
  appMode: getAppMode(),
  communityWizardModeStatus: false,
  newCommunity: false,
  step: 0,
  guideStep: 0,
  tutorialStep: 0,
  communityNotFound: false,
  userCreatedCommunityBefore: false,
  availableUserRoles: [],
  activeDashboard: undefined,
  activeGridMarketForm: EFormVariant.GridMarket,
  addNewLibrary: false,
  collaborationStep: 0,
  isBottomRightCopyrightVisible: false,
  language: 'en',
  screenMode: window.innerWidth > MOBILE_WIDTH ? EScreenMode.Desktop : EScreenMode.Mobile,
  screenOrientation: null,
};

const applicationSlice = createSlice({
  name: 'application',
  initialState: initialApplicationState as TApplicationState,
  reducers: {
    /* Modals */
    setModalAssetManagerActiveView(
      state,
      action: PayloadAction<TApplicationState['modalAssetManagerActiveView']>,
    ) {
      state.modalAssetManagerActiveView = action.payload;
    },
    setModalAssetManagerShowGuide(
      state,
      action: PayloadAction<TApplicationState['modalAssetManagerShowGuide']>,
    ) {
      state.modalAssetManagerShowGuide = action.payload;
    },
    setModalCommunitySummaryView(
      state,
      action: PayloadAction<TApplicationState['modalCommunitySummaryView']>,
    ) {
      state.modalCommunitySummaryView = action.payload;
    },
    setModalHouseSummaryView(
      state,
      action: PayloadAction<TApplicationState['modalHouseSummaryView']>,
    ) {
      state.modalHouseSummaryView = action.payload;
    },
    setModalHouseSummaryFormVariant(
      state,
      action: PayloadAction<TApplicationState['modalHouseSummaryFormVariant']>,
    ) {
      state.modalHouseSummaryFormVariant = action.payload;
    },
    /* Navigation Tabs */
    setActiveGridMarketTab(state, action: PayloadAction<TApplicationState['activeGridMarketTab']>) {
      state.activeGridMarketTab = action.payload;
    },
    setActiveSidebarMainTab(
      state,
      action: PayloadAction<TApplicationState['activeSidebarMainTab']>,
    ) {
      state.activeSidebarMainTab = action.payload;
    },
    setActiveSidebarSettingsSubTab(
      state,
      action: PayloadAction<TApplicationState['activeSidebarSettingsSubTab']>,
    ) {
      state.activeSidebarSettingsSubTab = action.payload;
    },
    setActiveResultsTab(state, action: PayloadAction<TApplicationState['activeResultsTab']>) {
      state.activeResultsTab = action.payload;
    },
    /* Selected Inputs */
    setSelectedMemberUuid(state, action: PayloadAction<TApplicationState['selectedMemberUuid']>) {
      state.selectedMemberUuid = action.payload;
    },
    setSelectedStartDatetime(
      state,
      action: PayloadAction<TApplicationState['selectedStartDatetime']>,
    ) {
      state.selectedStartDatetime = action.payload;
    },
    setSelectedEndDatetime(state, action: PayloadAction<TApplicationState['selectedEndDatetime']>) {
      state.selectedEndDatetime = action.payload;
    },
    setSelectedDataResolution(
      state,
      action: PayloadAction<TApplicationState['selectedDataResolution']>,
    ) {
      state.selectedDataResolution = action.payload;
    },
    /* Others */
    setIsCustomHouseAddingContinuation(
      state,
      action: PayloadAction<TApplicationState['isCustomHouseAddingContinuation']>,
    ) {
      state.isCustomHouseAddingContinuation = action.payload;
    },
    setCanaryWaitlistDismissed(
      state,
      action: PayloadAction<TApplicationState['canaryWaitlistDismissed']>,
    ) {
      state.canaryWaitlistDismissed = action.payload;
    },
    setModalMapAlertDismissed(
      state,
      action: PayloadAction<TApplicationState['modalMapAlertDismissed']>,
    ) {
      state.modalMapAlertDismissed = action.payload;
    },
    setSidebarExpanded(state, action: PayloadAction<TApplicationState['sidebarExpanded']>) {
      state.sidebarExpanded = action.payload;
    },
    setIsEmbed(state, action: PayloadAction<TApplicationState['isEmbed']>) {
      state.isEmbed = action.payload;
    },
    setAppMode(state, action: PayloadAction<TApplicationState['appMode']>) {
      state.appMode = action.payload;
    },
    setNewCommunity(state, action: PayloadAction<TApplicationState['newCommunity']>) {
      state.newCommunity = action.payload;
    },
    setOnBoardingStep(state, action: PayloadAction<TApplicationState['step']>) {
      state.step = action.payload;
    },
    setGuideStep(state, action: PayloadAction<TApplicationState['guideStep']>) {
      state.guideStep = action.payload;
    },
    setTutorialStep(state, action: PayloadAction<TApplicationState['tutorialStep']>) {
      state.tutorialStep = action.payload;
    },
    setCommunityNotFound(state, action: PayloadAction<TApplicationState['communityNotFound']>) {
      state.communityNotFound = action.payload;
    },
    setUserCreatedCommunityBefore(
      state,
      action: PayloadAction<TApplicationState['userCreatedCommunityBefore']>,
    ) {
      state.userCreatedCommunityBefore = action.payload;
    },
    setActiveDashboard(state, action: PayloadAction<TApplicationState['activeDashboard']>) {
      state.activeDashboard = action.payload;
    },
    setActiveGridMarketForm(
      state,
      action: PayloadAction<TApplicationState['activeGridMarketForm']>,
    ) {
      state.activeGridMarketForm = action.payload;
    },
    setAddNewLibrary(state, action: PayloadAction<TApplicationState['addNewLibrary']>) {
      state.addNewLibrary = action.payload;
    },
    setCollaborationStep(state, action: PayloadAction<TApplicationState['collaborationStep']>) {
      state.collaborationStep = action.payload;
    },
    setIsBottomRightCopyrightVisible(
      state,
      action: PayloadAction<TApplicationState['isBottomRightCopyrightVisible']>,
    ) {
      state.isBottomRightCopyrightVisible = action.payload;
    },
    setLanguage(state, action: PayloadAction<TApplicationState['language']>) {
      state.language = action.payload;
    },
    setScreenMode(state, action: PayloadAction<TApplicationState['screenMode']>) {
      state.screenMode = action.payload;
    },
    setScreenOrientation(state, action: PayloadAction<TApplicationState['screenOrientation']>) {
      state.screenOrientation = action.payload;
    },
  },
});

// Actions
export const {
  setCanaryWaitlistDismissed,
  setModalMapAlertDismissed,
  setIsCustomHouseAddingContinuation,
  setModalAssetManagerActiveView,
  setModalAssetManagerShowGuide,
  setModalCommunitySummaryView,
  setModalHouseSummaryFormVariant,
  setModalHouseSummaryView,
  setActiveSidebarMainTab,
  setActiveSidebarSettingsSubTab,
  setActiveResultsTab,
  setSidebarExpanded,
  setSelectedStartDatetime,
  setSelectedEndDatetime,
  setSelectedDataResolution,
  setIsEmbed,
  setAppMode,
  setNewCommunity,
  setOnBoardingStep,
  setGuideStep,
  setTutorialStep,
  setCommunityNotFound,
  setUserCreatedCommunityBefore,
  setActiveDashboard,
  setActiveGridMarketTab,
  setActiveGridMarketForm,
  setAddNewLibrary,
  setCollaborationStep,
  setIsBottomRightCopyrightVisible,
  setLanguage,
  setScreenMode,
  setScreenOrientation,
  setSelectedMemberUuid,
} = applicationSlice.actions;

// Reducer
export const applicationReducer = applicationSlice.reducer;
